import {api} from "@/shared/api";
import {HolidayCalculator, HolidayIndividualDays, IndividualHoliday} from "@/features/holidays/holidayIndividualDays";
import {htmlEscape} from "@/shared/utils/string";
import {Analytics} from "@/shared/analytics";
import {getCurrentTimeZoneId, normalizeTimeInput} from "@/shared/utils/datefunctions";
import {TimeZoneId} from "@/shared/timr";
import {ClientSegmentation} from "@/shared/timrTypes";
import {Combobox} from "@/shared/ui/combobox";

export interface SetupWizardConfig {
	timeZoneIds: TimeZoneId[];
	currentTimeZoneId: string;
	selectedIndustry: string | null;
	industryOptions: {
		id: string;
		label: string;
	}[];
}

export class SetupWizardPage {
	public static KEY_ENTER = 13;

	constructor() {
		$(document).on("keypress", ":input:not(textarea):not([type=submit])", (event) => {
			if (event.keyCode === SetupWizardPage.KEY_ENTER) {
				event.preventDefault();
				$("#setupNextButton").click();
			}
		});
	}

	public initStart(config: SetupWizardConfig) {
		$("#setupCancelButton").on("click", () => {
			Analytics.trackEvent("SetupWizard - Canceled", "Onboarding");
		});

		// pass browser default timezone
		$("#timeZoneId").val(getCurrentTimeZoneId());

		this.updateClientSegmentationCompanySize();
		$(`input[type="radio"][name="clientSegmentation"]`).change(e => {
			this.updateClientSegmentationCompanySize();
			$(e.target)
				.closest(".form-group").removeClass("has-error")
				.find(".help-block").hide();
		});

		// change users language/locale on changing client language
		$("select[name=language]").change(e => {
			api("changeLanguage.json")
				.post({language: $(e.target).val() as string})
				.done(() => $("#setupWizardForm").submit()); // page is reloaded via submit that selected values are saved
		});

		this.initIndustryCombobox(config);
	}

	private initIndustryCombobox(config: SetupWizardConfig) {
		const industryCombobox = new Combobox({
			items: config.industryOptions,
			formatter: item => item.label,
			idElement: $("#setupIndustryId"),
			filterElement: $("#setupIndustryFilter"),
			caretElement: $("#setupIndustryCaret"),
			emptyMessage: messages["setup.start.industry.default"],
			notFoundMessage: messages["setup.start.industry.notFound"]
		});
		industryCombobox.setSelectedItemById(config.selectedIndustry);
	}

	/**
	 * Updates the client segmentation based on company size selection. This method reads the selected client segmentation option,
	 * toggles the visibility of the employee selection dropdown, and adjusts the corresponding value based on the company size
	 * type.
	 *
	 * @return {void} This method does not return any value.
	 */
	private updateClientSegmentationCompanySize() {
		const clientSegmentation = $(`input[type="radio"][name="clientSegmentation"]:checked`).val() as ClientSegmentation;

		$(`select[name="employees"]`).closest(".form-group")
			.css("visibility", clientSegmentation === ClientSegmentation.COMPANY_TEAM ? "visible" : "hidden"); // use visibility to avoid flickering through toggle
	}

	public initRequirements() {
		const featureCardSelector = "[data-feature-card]";
		const suboptionSelector = "[data-feature-suboption]";
		const checkboxSelector = `${featureCardSelector} input[type="checkbox"], ${suboptionSelector} input[type="checkbox"]`;

		// Update the styling of feature cards based on the selection state of their associated checkboxes (border/highlight)
		const updateSelectionStateStyling = () => {
			$(featureCardSelector).each((_, element) => {
				const card = $(element);
				const checkbox = card.find(`input[type="checkbox"]`);
				card.toggleClass("is-selected", checkbox.is(":checked"));
			});
		};

		// Listen to checkbox changes to update parent/subfeature relationships (select parent if subfeature is selected; deselect subfeatures if parent is deselected)
		$(checkboxSelector).change((event) => {
			const checkbox = $(event.target);
			if (checkbox.is(":checked")) {
				const parentId = checkbox.data("parent-id") as string;

				if (parentId) {
					const parentFeatureCheckbox = $(`#${parentId}`);
					if (parentFeatureCheckbox.length && !parentFeatureCheckbox.is(":checked")) {
						parentFeatureCheckbox.prop("checked", true);
					}
				}
			} else {
				const checkboxId = checkbox.attr("id");
				if (checkboxId) {
					$(`[data-parent-id="${checkboxId}"]`).each((_, child) => {
						const subFeatureCheckbox = $(child);
						if (subFeatureCheckbox.is(":checked")) {
							subFeatureCheckbox.prop("checked", false);
						}
					});
				}
			}
			updateSelectionStateStyling();
		});

		updateSelectionStateStyling();
	}

	public initWorkingTime() {
		const setupStartTimeToday = $("#setupStartTimeToday");
		setupStartTimeToday.blur(() => normalizeTimeInput(setupStartTimeToday));
		normalizeTimeInput(setupStartTimeToday);

		this.initHolidayCalendarAutoGeneration();
	}

	public initProjectTime() {
		const customerName = $("#setupCustomerName");
		const projectName = $("#setupProjectName");
		const taskName = $("#setupTaskName");
		const nameInputs = $("#nameInputs");

		const treeElement = $("#setupTaskTree");

		// @ts-expect-error jquery.fancytree type definition is wrong
		treeElement.fancytree({
			toggleEffect: false,
			source: {
				children: []
			}
		});

		const tree = treeElement.fancytree("getTree");

		if (tree.getRootNode !== undefined) {
			function updateDisplay() {
				let node = tree.getRootNode();
				node.removeChildren();

				function addTreeNode(parent: Fancytree.FancytreeNode, label: string): Fancytree.FancytreeNode {
					if (!label) {
						return parent;
					} else {
						return node.addChildren({
							title: htmlEscape(label),
							icon: false
						});
					}
				}

				node = addTreeNode(node, customerName.val() as string);
				node = addTreeNode(node, projectName.val() as string);
				node = addTreeNode(node, taskName.val() as string);
				tree.visit((visitedNode) => visitedNode.setExpanded());

				const hasInput = !!(customerName.val() || projectName.val() || taskName.val());

				$("#setupTaskTreeHeader").toggle(hasInput);
				if (hasInput) {
					nameInputs.removeClass("has-error");
					customerName.removeClass("has-error");
					projectName.removeClass("has-error");
					taskName.removeClass("has-error");
				} else {
					nameInputs.addClass("has-error");
					customerName.addClass("has-error");
					projectName.addClass("has-error");
					taskName.addClass("has-error");
				}
			}

			customerName.add(projectName).add(taskName).keyup(updateDisplay);
			updateDisplay();
		}
	}

	public initDrivelog() {
		const carName = $("#setupCarName");
		const carPlate = $("#setupCarPlate");

		carName.keyup(e => {
			if (e.which !== 9) { // not tab key
				const hasCarName = !!carName.val();
				carName.parent().parent().toggleClass("has-error", !hasCarName);
				carName.next("span.help-block").toggle(!hasCarName);
			}
		});

		carPlate.keyup(e => {
			if (e.which !== 9) { // not tab key
				const hasCarPlate = !!carPlate.val();
				carPlate.parent().parent().toggleClass("has-error", !hasCarPlate);
				carPlate.next("span.help-block").toggle(!hasCarPlate);
			}
		});
	}

	private initHolidayCalendarAutoGeneration() {
		const individualHolidaySettings = document.getElementById("setupIndividualHolidaySettings") as HolidayIndividualDays | null;
		if (!individualHolidaySettings) {
			return;
		}

		const holidayCalendarField = $(`[name="holidayCalendar"]`);
		if (!holidayCalendarField.length) {
			return;
		}

		const applyHolidayCalculatorInfo = (info?: HolidayCalculator | null) => {
			individualHolidaySettings.updateHoliday(IndividualHoliday.CHRISTMAS, {include: info?.includesChristmasEve === true});
			individualHolidaySettings.updateHoliday(IndividualHoliday.NEW_YEARS, {include: info?.includesNewYearsEve === true});
		};

		const updateIndividualHolidayVisibility = (calculatorBean: string) => {
			api("admin/holidayCalculator.json")
				.get({calculatorBean})
				.done((response: HolidayCalculator) => {
					applyHolidayCalculatorInfo(response);
				})
				.fail(() => {
					applyHolidayCalculatorInfo();
				});
		};

		// on initial load, apply data of initial calculator info (jsp) if available
		const initialCalculatorInfo = individualHolidaySettings.getInitialCalculatorInfo();
		if (initialCalculatorInfo) {
			applyHolidayCalculatorInfo(initialCalculatorInfo);
		}

		// on change of holiday calendar selection, update individual holiday settings
		if (holidayCalendarField.is("select")) {
			holidayCalendarField.change((event) => {
				updateIndividualHolidayVisibility($(event.target).val() as string);
			});
		}
	}

}
